import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import { Button } from "../../common/Button";
import { failed, success } from "../../common/Toastify";
import {
  createAvailabilityException,
  updateAvailabilityException,
} from "../../api/Scheduling/availabilityExceptions";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import { useServiceCategoriesQuery } from "../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import WindowCategoryPicker from "./WindowCategoryPicker";
import ShiftBreaksFields, { createBreakBlocks } from "./ShiftBreaksFields";
import { shiftBreaksToBlockRanges } from "./time";
import { timeZoneLabel } from "./timeZones";

const replacementWindow = (categoryIds) => ({
  startTime: "09:00",
  endTime: "17:00",
  mode: "regular",
  serviceCategoryIds: categoryIds,
  breaks: [],
});

const ExceptionForm = ({ show, onHide, organizationRef, schedule, rules = [], exception, onSaved, initialDate }) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const categoryIds = schedule?.serviceCategoryIds || [];
  const isEdit = !!exception?.availabilityExceptionId;
  const { data: categories = [] } = useServiceCategoriesQuery({ scope: "descendants", practitionerRef: schedule?.practitionerRef,
    organizationRef,
    active: true,
    enabled: !!organizationRef && show,
  });
  const categoryOptions = useMemo(
    () =>
      categories
        .filter((category) => categoryIds.includes(category.serviceCategoryId))
        .map((category) => ({
          name: `${category.name} (${category.durationMinutes} min)`,
          value: category.serviceCategoryId,
          organizationRef: category.organizationRef, organizationName: category.organizationName || category.organizationRef,
        })),
    [categories, categoryIds]
  );

  const formik = useFormik({
    initialValues: {
      availabilityRuleId: "",
      localDate: initialDate || "",
      type: "closed",
      windows: [replacementWindow(categoryIds)],
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!values.availabilityRuleId) {
        failed("Pick the rule that covers this date.");
        return;
      }
      const body = {
        organizationRef,
        availabilityRuleId: values.availabilityRuleId,
        localDate: values.localDate,
        type: values.type,
      };
      const blockRanges = [];
      if (values.type === "replace") {
        if (!values.windows.length || values.windows.length > 8) {
          failed("Replace must send 1 to 8 windows.");
          return;
        }
        if (values.windows.some((window) => !window.serviceCategoryIds?.length)) {
          failed("Each replacement window needs at least one service category.");
          return;
        }
        for (const window of values.windows) {
          const breakResult = shiftBreaksToBlockRanges(window, values.localDate, schedule?.timezoneName || "UTC");
          if (breakResult.error) {
            failed(breakResult.error);
            return;
          }
          blockRanges.push(...breakResult.ranges);
        }
        body.windows = values.windows.map((window) => ({
          startTime: window.startTime,
          endTime: window.endTime,
          mode: window.mode || "regular",
          serviceCategoryIds: window.serviceCategoryIds,
        }));
      }
      setBtnLoading(true);
      let hoursSaved = false;
      try {
        if (isEdit) {
          await updateAvailabilityException({
            organizationRef,
            availabilityExceptionId: exception.availabilityExceptionId,
            type: values.type,
            windows: body.windows,
            expectedVersion: exception.version,
          });
        } else {
          await createAvailabilityException(body);
        }
        hoursSaved = true;
        await createBreakBlocks({
          organizationRef,
          scheduleId: schedule.scheduleId,
          ranges: blockRanges,
        });
        success(isEdit ? "Exception updated" : "Exception created");
        onSaved?.();
        onHide();
      } catch (error) {
        failed(
          hoursSaved && blockRanges.length
            ? `Hours saved, but a break could not be added. ${schedulingErrorMessage(error)}`
            : schedulingErrorMessage(error)
        );
        if (hoursSaved) {
          onSaved?.();
          onHide();
        }
      } finally {
        setBtnLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!show) return;
    if (exception) {
      formik.setValues({
        availabilityRuleId: exception.availabilityRuleId,
        localDate: exception.localDate,
        type: exception.type,
        windows: exception.windows?.length
          ? exception.windows.map((window) => ({ ...window, breaks: window.breaks || [] }))
          : [replacementWindow(categoryIds)],
      });
    } else {
      const covering = rules.find(
        (rule) =>
          rule.active &&
          (!initialDate || (rule.effectiveFrom <= initialDate && rule.effectiveTo >= initialDate))
      );
      formik.setValues({
        availabilityRuleId: covering?.availabilityRuleId || rules[0]?.availabilityRuleId || "",
        localDate: initialDate || "",
        type: "closed",
        windows: [replacementWindow(categoryIds)],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, exception, initialDate]);

  const coveringRules = rules.filter(
    (rule) =>
      rule.active &&
      (!formik.values.localDate ||
        (rule.effectiveFrom <= formik.values.localDate && rule.effectiveTo >= formik.values.localDate))
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" className="custom-dialog scheduling-dialog">
      <Modal.Header closeButton className="border-0">
        <Modal.Title>{isEdit ? "Edit date change" : "Change hours for a date"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="schedule-zone-note">Schedule time: {timeZoneLabel(schedule?.timezoneName)}</p>
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          <Row>
            <Col>
              <label>Date</label>
              <input
                type="date"
                className="form-control"
                value={formik.values.localDate}
                disabled={isEdit}
                onChange={(event) => formik.setFieldValue("localDate", event.target.value)}
              />
            </Col>
            <Col>
              <label>Work schedule for this date</label>
              <select
                className="form-control"
                value={formik.values.availabilityRuleId}
                disabled={isEdit}
                onChange={(event) => formik.setFieldValue("availabilityRuleId", event.target.value)}
              >
                <option value="">Select work schedule</option>
                {coveringRules.map((rule) => (
                  <option key={rule.availabilityRuleId} value={rule.availabilityRuleId}>
                    {rule.effectiveFrom} – {rule.effectiveTo}
                  </option>
                ))}
              </select>
            </Col>
            <Col>
              <label>Change to make</label>
              <select
                className="form-control"
                value={formik.values.type}
                onChange={(event) => formik.setFieldValue("type", event.target.value)}
              >
                <option value="closed">Day off · no appointments</option>
                <option value="replace">Different working hours</option>
              </select>
            </Col>
          </Row>
          {formik.values.type === "replace" ? (
            <div className="scheduling-window-list">
              {formik.values.windows.map((window, index) => (
                <div key={index} className="scheduling-window-row">
                  {formik.values.windows.length > 1 ? (
                    <div className="scheduling-window-row-header">
                      <Button
                        variant="secondary"
                        onClick={() =>
                          formik.setFieldValue(
                            "windows",
                            formik.values.windows.filter((_, idx) => idx !== index)
                          )
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  ) : null}
                  <div className="scheduling-window-fields">
                    <Row className="align-items-end g-2">
                      <Col md>
                        <label>Start</label>
                        <input
                          type="time"
                          className="form-control"
                          value={window.startTime}
                          onChange={(event) => {
                            const next = formik.values.windows.slice();
                            next[index] = { ...next[index], startTime: event.target.value };
                            formik.setFieldValue("windows", next);
                          }}
                        />
                      </Col>
                    <Col md>
                      <label>End</label>
                      <input
                        type="time"
                        className="form-control"
                        value={window.endTime}
                        onChange={(event) => {
                          const next = formik.values.windows.slice();
                          next[index] = { ...next[index], endTime: event.target.value };
                          formik.setFieldValue("windows", next);
                        }}
                      />
                    </Col>
                  </Row>
                  </div>
                  <div className="scheduling-window-categories-block">
                    <WindowCategoryPicker
                      options={categoryOptions}
                      value={window.serviceCategoryIds || []}
                      onChange={(nextIds) => {
                        const next = formik.values.windows.slice();
                        next[index] = { ...next[index], serviceCategoryIds: nextIds };
                        formik.setFieldValue("windows", next);
                      }}
                    />
                  </div>
                  <ShiftBreaksFields
                    breaks={window.breaks || []}
                    onChange={(breaks) => {
                      const next = formik.values.windows.slice();
                      next[index] = { ...next[index], breaks };
                      formik.setFieldValue("windows", next);
                    }}
                    removeAriaLabel={(pauseIndex) => `Remove break ${pauseIndex + 1} from replacement window ${index + 1}`}
                  />
                </div>
              ))}
            </div>
          ) : null}
          {formik.values.type === "replace" ? (
            <div className="scheduling-window-add">
              <Button
                variant="secondary"
                disabled={formik.values.windows.length >= 8}
                onClick={() =>
                  formik.setFieldValue("windows", [
                    ...formik.values.windows,
                    replacementWindow(categoryIds),
                  ])
                }
              >
                Add replacement window
              </Button>
            </div>
          ) : null}
          <div className="btn-wrap" style={{ display: "flex", marginTop: "1rem" }}>
            <Button variant="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading}>
              Save
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default ExceptionForm;
